import {
  Accordion,
  Alert,
  Code,
  Group,
  NumberInput,
  Select,
  Stack,
  Text,
  Textarea,
  TextInput,
} from "@mantine/core";
import { IconInfoCircle } from "@tabler/icons-react";
import type { BookStrategy, NumberStrategy, SeriesStrategy } from "@/types";

// =============================================================================
// Series Strategy Configuration
// =============================================================================

export interface SeriesStrategyData {
  value: SeriesStrategy;
  label: string;
  description: string;
  example: string;
  hasConfig: boolean;
}

export const SERIES_STRATEGIES: SeriesStrategyData[] = [
  {
    value: "series_volume",
    label: "Series-Volume (Recommended)",
    description:
      "Direct child folders of library = series, files in folders = books. Best for comics and simple folder structures.",
    example: "/library/Batman/Batman #001.cbz → Series: Batman",
    hasConfig: false,
  },
  {
    value: "series_volume_chapter",
    label: "Series-Volume-Chapter",
    description:
      "Parent folder = series, child folders = volumes/arcs, files = chapters. Best for chapter-based manga.",
    example: "/library/One Piece/Volume 01/Chapter 001.cbz → Series: One Piece",
    hasConfig: false,
  },
  {
    value: "flat",
    label: "Flat Structure",
    description:
      "All files at library root, series detected from filename patterns or metadata. Best for automated downloaders.",
    example: "/library/[One Piece] v01.cbz → Series: One Piece",
    hasConfig: true,
  },
  {
    value: "publisher_hierarchy",
    label: "Publisher Hierarchy",
    description:
      "Skip first N levels (publisher/year folders), then apply series-volume rules. Best for collections organized by publisher.",
    example: "/library/Marvel/Spider-Man/Amazing #001.cbz → Series: Spider-Man",
    hasConfig: true,
  },
  {
    value: "calibre",
    label: "Calibre Library",
    description:
      "Author folder → Book title folder → book files. Direct Calibre library compatibility.",
    example:
      "/library/Brandon Sanderson/Mistborn (1)/Mistborn.epub → Book: Mistborn",
    hasConfig: true,
  },
  {
    value: "custom",
    label: "Custom (Advanced)",
    description:
      "User-defined regex patterns for series detection. For unique organizational patterns.",
    example: "Define your own regex pattern to match series names",
    hasConfig: true,
  },
];

// =============================================================================
// Book Strategy Configuration
// =============================================================================

export interface BookStrategyData {
  value: BookStrategy;
  label: string;
  description: string;
  hasConfig?: boolean;
}

export const BOOK_STRATEGIES: BookStrategyData[] = [
  {
    value: "filename",
    label: "Filename (Recommended)",
    description:
      "Parses title, volume, and chapter from the filename. Volume and chapter are detected via `v\\d+` and `c\\d+` patterns. Predictable and Komga-compatible.",
  },
  {
    value: "metadata_first",
    label: "Metadata First",
    description:
      "Uses ComicInfo.xml for title, volume, and chapter. Falls back to the filename only for the title when ComicInfo is missing; volume and chapter stay empty.",
  },
  {
    value: "smart",
    label: "Smart Detection",
    description:
      "Reads ComicInfo.xml first, then falls back to filename patterns for any field ComicInfo did not supply (title, volume, chapter).",
  },
  {
    value: "series_name",
    label: "Generated Name",
    description:
      "Builds the title from series + volume + chapter context. Volume and chapter come from series detection (e.g., the series-volume-chapter folder strategy).",
  },
  {
    value: "custom",
    label: "Custom (Regex)",
    description:
      "User-defined regex with named groups `series`, `volume`, `chapter`, `title` to extract metadata from filenames. For unique naming conventions.",
    hasConfig: true,
  },
];

// =============================================================================
// Flat Strategy Config
// =============================================================================

export interface FlatStrategyConfig {
  filenamePatterns?: string[];
  requireMetadata?: boolean;
}

interface FlatConfigEditorProps {
  config: FlatStrategyConfig;
  onChange: (config: FlatStrategyConfig) => void;
  disabled?: boolean;
}

function FlatConfigEditor({
  config,
  onChange,
  disabled = false,
}: FlatConfigEditorProps) {
  const patternsText = (config.filenamePatterns || []).join("\n");

  return (
    <Stack gap="sm">
      <Textarea
        label="Filename Patterns"
        description="Regex patterns to extract series name from filename (one per line). First capture group = series name."
        placeholder={"\\[([^\\]]+)\\]\n^([^-]+) -\n^([^_]+)_"}
        value={patternsText}
        onChange={(e) =>
          onChange({
            ...config,
            filenamePatterns: e.currentTarget.value
              .split("\n")
              .filter((p) => p.trim()),
          })
        }
        minRows={3}
        styles={{ input: { fontFamily: "monospace" } }}
        disabled={disabled}
      />
      <Alert icon={<IconInfoCircle size={16} />} color="blue" variant="light">
        <Text size="sm">
          Default patterns: <Code>[Series Name]</Code>,{" "}
          <Code>Series Name -</Code>, <Code>Series_Name</Code>
        </Text>
      </Alert>
    </Stack>
  );
}

// =============================================================================
// Publisher Hierarchy Config
// =============================================================================

export interface PublisherHierarchyConfig {
  skipDepth?: number;
  storeSkippedAs?: string;
}

interface PublisherHierarchyConfigEditorProps {
  config: PublisherHierarchyConfig;
  onChange: (config: PublisherHierarchyConfig) => void;
  disabled?: boolean;
}

function PublisherHierarchyConfigEditor({
  config,
  onChange,
  disabled = false,
}: PublisherHierarchyConfigEditorProps) {
  return (
    <Stack gap="sm">
      <NumberInput
        label="Skip Depth"
        description="Number of folder levels to skip before detecting series"
        placeholder="1"
        value={config.skipDepth ?? 1}
        onChange={(value) =>
          onChange({
            ...config,
            skipDepth: typeof value === "number" ? value : 1,
          })
        }
        min={1}
        max={5}
        disabled={disabled}
      />
      <TextInput
        label="Store Skipped As"
        description="Metadata field to store skipped folder name (e.g., 'publisher')"
        placeholder="publisher"
        value={config.storeSkippedAs || ""}
        onChange={(e) =>
          onChange({
            ...config,
            storeSkippedAs: e.currentTarget.value || undefined,
          })
        }
        disabled={disabled}
      />
    </Stack>
  );
}

// =============================================================================
// Calibre Strategy Config
// =============================================================================

export interface CalibreStrategyConfig {
  stripIdSuffix?: boolean;
  seriesMode?: "standalone" | "by_author" | "from_metadata";
  readOpfMetadata?: boolean;
  authorFromFolder?: boolean;
}

interface CalibreConfigEditorProps {
  config: CalibreStrategyConfig;
  onChange: (config: CalibreStrategyConfig) => void;
  disabled?: boolean;
}

function CalibreConfigEditor({
  config,
  onChange,
  disabled = false,
}: CalibreConfigEditorProps) {
  return (
    <Stack gap="sm">
      <Select
        label="Series Grouping Mode"
        description="How to group books into series"
        data={[
          {
            value: "standalone",
            label: "Standalone - Each book is its own series",
          },
          {
            value: "by_author",
            label: "By Author - Group all books by same author",
          },
          {
            value: "from_metadata",
            label:
              "From Metadata - Use series field from OPF/embedded metadata",
          },
        ]}
        value={config.seriesMode || "from_metadata"}
        onChange={(value) =>
          onChange({
            ...config,
            seriesMode:
              (value as CalibreStrategyConfig["seriesMode"]) || "from_metadata",
          })
        }
        comboboxProps={{ zIndex: 1001 }}
        disabled={disabled}
      />
      <Alert icon={<IconInfoCircle size={16} />} color="blue" variant="light">
        <Text size="sm">
          Calibre folder ID suffixes (e.g., " (123)") are automatically
          stripped. Author names are extracted from folder structure.
        </Text>
      </Alert>
    </Stack>
  );
}

// =============================================================================
// Custom Strategy Config
// =============================================================================

export interface CustomStrategyConfig {
  pattern?: string;
  seriesNameTemplate?: string;
}

interface CustomConfigEditorProps {
  config: CustomStrategyConfig;
  onChange: (config: CustomStrategyConfig) => void;
  disabled?: boolean;
}

function CustomConfigEditor({
  config,
  onChange,
  disabled = false,
}: CustomConfigEditorProps) {
  return (
    <Stack gap="sm">
      <TextInput
        label="Pattern"
        description="Regex pattern matched against relative path from library root. Use named groups."
        placeholder="^(?P<publisher>[^/]+)/(?P<series>[^/]+)/(?P<book>.+)\.(cbz|cbr|epub|pdf)$"
        value={config.pattern || ""}
        onChange={(e) =>
          onChange({
            ...config,
            pattern: e.currentTarget.value || undefined,
          })
        }
        styles={{ input: { fontFamily: "monospace" } }}
        disabled={disabled}
      />
      <TextInput
        label="Series Name Template"
        description="Template to construct series name from captured groups"
        placeholder="{publisher} - {series}"
        value={config.seriesNameTemplate || ""}
        onChange={(e) =>
          onChange({
            ...config,
            seriesNameTemplate: e.currentTarget.value || undefined,
          })
        }
        styles={{ input: { fontFamily: "monospace" } }}
        disabled={disabled}
      />
      <Accordion variant="separated">
        <Accordion.Item value="help">
          <Accordion.Control>Named Groups Reference</Accordion.Control>
          <Accordion.Panel>
            <Stack gap="xs">
              <Text size="sm">
                <Code>(?P&lt;series&gt;...)</Code> - Required: identifies the
                series name
              </Text>
              <Text size="sm">
                <Code>(?P&lt;publisher&gt;...)</Code> - Optional: stored as
                series metadata
              </Text>
              <Text size="sm">
                <Code>(?P&lt;book&gt;...)</Code> - Optional: identifies the book
                filename portion
              </Text>
            </Stack>
          </Accordion.Panel>
        </Accordion.Item>
      </Accordion>
    </Stack>
  );
}

// =============================================================================
// Series Strategy Selector Component
// =============================================================================

export interface SeriesStrategySelectorProps {
  value: SeriesStrategy;
  onChange: (value: SeriesStrategy) => void;
  config: Record<string, unknown>;
  onConfigChange: (config: Record<string, unknown>) => void;
  disabled?: boolean;
}

export function SeriesStrategySelector({
  value,
  onChange,
  config,
  onConfigChange,
  disabled = false,
}: SeriesStrategySelectorProps) {
  const selectedStrategy = SERIES_STRATEGIES.find((s) => s.value === value);

  const renderConfigEditor = () => {
    switch (value) {
      case "flat":
        return (
          <FlatConfigEditor
            config={config as FlatStrategyConfig}
            onChange={onConfigChange as (config: FlatStrategyConfig) => void}
            disabled={disabled}
          />
        );
      case "publisher_hierarchy":
        return (
          <PublisherHierarchyConfigEditor
            config={config as PublisherHierarchyConfig}
            onChange={
              onConfigChange as (config: PublisherHierarchyConfig) => void
            }
            disabled={disabled}
          />
        );
      case "calibre":
        return (
          <CalibreConfigEditor
            config={config as CalibreStrategyConfig}
            onChange={onConfigChange as (config: CalibreStrategyConfig) => void}
            disabled={disabled}
          />
        );
      case "custom":
        return (
          <CustomConfigEditor
            config={config as CustomStrategyConfig}
            onChange={onConfigChange as (config: CustomStrategyConfig) => void}
            disabled={disabled}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Stack gap="md">
      <Select
        label="Series Detection Strategy"
        description="How series are detected from your folder structure"
        data={SERIES_STRATEGIES.map((s) => ({
          value: s.value,
          label: s.label,
        }))}
        value={value}
        onChange={(v) => {
          if (v) {
            onChange(v as SeriesStrategy);
            // Reset config when strategy changes
            onConfigChange({});
          }
        }}
        disabled={disabled}
        comboboxProps={{ zIndex: 1001 }}
      />

      {selectedStrategy && (
        <Alert icon={<IconInfoCircle size={16} />} color="blue" variant="light">
          <Stack gap="xs">
            <Text size="sm">{selectedStrategy.description}</Text>
            <Group gap="xs">
              <Text size="xs" fw={500} c="dimmed">
                Example:
              </Text>
              <Code>{selectedStrategy.example}</Code>
            </Group>
          </Stack>
        </Alert>
      )}

      {selectedStrategy?.hasConfig && renderConfigEditor()}
    </Stack>
  );
}

// =============================================================================
// Custom Book Strategy Config
// =============================================================================

export interface CustomBookConfig {
  pattern?: string;
  titleTemplate?: string;
  fallback?: string;
}

interface CustomBookConfigEditorProps {
  config: CustomBookConfig;
  onChange: (config: CustomBookConfig) => void;
  disabled?: boolean;
}

function CustomBookConfigEditor({
  config,
  onChange,
  disabled = false,
}: CustomBookConfigEditorProps) {
  return (
    <Stack gap="sm">
      <TextInput
        label="Regex Pattern"
        description="Regex with named capture groups matched against the filename (without extension)."
        placeholder="(?P<series>.+?)_v(?P<volume>\d+)_c(?P<chapter>\d+)"
        value={config.pattern || ""}
        onChange={(e) =>
          onChange({
            ...config,
            pattern: e.currentTarget.value || undefined,
          })
        }
        styles={{ input: { fontFamily: "monospace" } }}
        disabled={disabled}
      />
      <TextInput
        label="Title Template"
        description="Template for constructing the display title from captured groups (optional)."
        placeholder="{series} v.{volume} c.{chapter}"
        value={config.titleTemplate || ""}
        onChange={(e) =>
          onChange({
            ...config,
            titleTemplate: e.currentTarget.value || undefined,
          })
        }
        styles={{ input: { fontFamily: "monospace" } }}
        disabled={disabled}
      />
      <Select
        label="Fallback Strategy"
        description="Strategy to use when the regex pattern does not match"
        data={[
          { value: "filename", label: "Filename" },
          { value: "metadata_first", label: "Metadata First" },
          { value: "smart", label: "Smart Detection" },
          { value: "series_name", label: "Generated Name" },
        ]}
        value={config.fallback || "filename"}
        onChange={(value) =>
          onChange({
            ...config,
            fallback: value || "filename",
          })
        }
        comboboxProps={{ zIndex: 1001 }}
        disabled={disabled}
      />
      <Accordion variant="separated">
        <Accordion.Item value="help">
          <Accordion.Control>Capture Groups & Placeholders</Accordion.Control>
          <Accordion.Panel>
            <Stack gap="xs">
              <Text size="sm" fw={500}>
                Named Capture Groups
              </Text>
              <Text size="sm">
                <Code>(?P&lt;title&gt;...)</Code> - Book title
              </Text>
              <Text size="sm">
                <Code>(?P&lt;series&gt;...)</Code> - Series name
              </Text>
              <Text size="sm">
                <Code>(?P&lt;volume&gt;...)</Code> - Volume number
              </Text>
              <Text size="sm">
                <Code>(?P&lt;chapter&gt;...)</Code> - Chapter number
              </Text>
              <Text size="sm" fw={500} mt="xs">
                Template Placeholders
              </Text>
              <Text size="sm">
                <Code>{"{title}"}</Code>, <Code>{"{series}"}</Code>,{" "}
                <Code>{"{volume}"}</Code>, <Code>{"{chapter}"}</Code>,{" "}
                <Code>{"{filename}"}</Code>
              </Text>
            </Stack>
          </Accordion.Panel>
        </Accordion.Item>
      </Accordion>
    </Stack>
  );
}

// =============================================================================
// Book Strategy Selector Component
// =============================================================================

export interface BookStrategySelectorProps {
  value: BookStrategy;
  onChange: (value: BookStrategy) => void;
  config?: CustomBookConfig;
  onConfigChange?: (config: CustomBookConfig) => void;
  disabled?: boolean;
}

export function BookStrategySelector({
  value,
  onChange,
  config,
  onConfigChange,
  disabled = false,
}: BookStrategySelectorProps) {
  const selectedStrategy = BOOK_STRATEGIES.find((s) => s.value === value);

  return (
    <Stack gap="md">
      <Select
        label="Book Metadata Strategy"
        description="How book metadata (title, volume, chapter) is extracted from files"
        data={BOOK_STRATEGIES.map((s) => ({
          value: s.value,
          label: s.label,
        }))}
        value={value}
        onChange={(v) => {
          if (v) {
            onChange(v as BookStrategy);
            if (onConfigChange) {
              onConfigChange({});
            }
          }
        }}
        disabled={disabled}
        comboboxProps={{ zIndex: 1001 }}
      />

      {selectedStrategy && (
        <Alert icon={<IconInfoCircle size={16} />} color="gray" variant="light">
          <Text size="sm">{selectedStrategy.description}</Text>
        </Alert>
      )}

      {value === "custom" && onConfigChange && (
        <CustomBookConfigEditor
          config={config || {}}
          onChange={onConfigChange}
          disabled={disabled}
        />
      )}
    </Stack>
  );
}

// =============================================================================
// Number Strategy Configuration
// =============================================================================

export interface NumberStrategyData {
  value: NumberStrategy;
  label: string;
  description: string;
}

export const NUMBER_STRATEGIES: NumberStrategyData[] = [
  {
    value: "file_order",
    label: "File Order (Recommended)",
    description:
      "Book number = position in alphabetically sorted file list. Komga-compatible and works with any naming.",
  },
  {
    value: "metadata",
    label: "Metadata Only",
    description:
      "Use ComicInfo <Number> field only. Files without metadata get no number.",
  },
  {
    value: "filename",
    label: "Filename Patterns",
    description:
      "Parse number from filename patterns (#001, v01, c001, Chapter 001). Ignores metadata.",
  },
  {
    value: "smart",
    label: "Smart Detection",
    description:
      "Fallback chain: metadata → filename patterns → file order. Best coverage with graceful degradation.",
  },
];

// =============================================================================
// Number Strategy Selector Component
// =============================================================================

export interface NumberStrategySelectorProps {
  value: NumberStrategy;
  onChange: (value: NumberStrategy) => void;
  disabled?: boolean;
}

export function NumberStrategySelector({
  value,
  onChange,
  disabled = false,
}: NumberStrategySelectorProps) {
  const selectedStrategy = NUMBER_STRATEGIES.find((s) => s.value === value);

  return (
    <Stack gap="md">
      <Select
        label="Book Number Strategy"
        description="How book numbers (sort order) are determined"
        data={NUMBER_STRATEGIES.map((s) => ({
          value: s.value,
          label: s.label,
        }))}
        value={value}
        onChange={(v) => {
          if (v) {
            onChange(v as NumberStrategy);
          }
        }}
        disabled={disabled}
        comboboxProps={{ zIndex: 1001 }}
      />

      {selectedStrategy && (
        <Alert icon={<IconInfoCircle size={16} />} color="gray" variant="light">
          <Text size="sm">{selectedStrategy.description}</Text>
        </Alert>
      )}
    </Stack>
  );
}
